import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'src/utils/Hooks';
import { BeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_TABS, useBeaconRoutes } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';

/**
 * Hook that validates the current tab/subtab combination and redirects to a valid
 * subtab if the current one is hidden/unavailable (e.g., when switching retailers).
 *
 * This follows the same pattern as useAtlasV2TabTreatmentRedirect.
 */
export const useBeaconSubtabValidationRedirect = () => {
  const history = useHistory();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlTab = searchParams.get('tab') || '';
  const urlSubtab = searchParams.get('subtab') || '';

  const appName = getAppName();
  const { currentRoute, currentSubtab, routes } = useBeaconRoutes();
  const advertisingRoutes = routes?.advertisingRoutes ?? [];

  const isHiddenAdvertisingWorkflowSubtab = useMemo(() => {
    if (urlTab !== BEACON_TABS.ADVERTISING || !urlSubtab) return false;

    return advertisingRoutes.some((route) => {
      if (!route.hidden || route.tabMappedFrom !== BEACON_TABS.ADVERTISING) return false;
      return route.childSubtabs?.some((subtab) => subtab.value === urlSubtab);
    });
  }, [advertisingRoutes, urlTab, urlSubtab]);

  useEffect(() => {
    // Only apply to Beacon app
    if (appName !== AppName.Beacon) return;

    // Skip if no current route found (shouldn't happen but safety check)
    if (!currentRoute) return;

    // Skip if already redirecting
    if (isRedirecting) return;

    if (isHiddenAdvertisingWorkflowSubtab) return;

    // Check if the URL subtab matches the computed valid subtab
    // If they differ, it means the URL subtab is invalid/hidden and we need to redirect
    if (urlSubtab && currentSubtab && urlSubtab !== currentSubtab) {
      // Get the correct tab from the current route
      const targetTab = currentRoute.tabMappedFrom || urlTab;

      setIsRedirecting(true);

      const beaconNavigator = new BeaconNavigator(new URLSearchParams(location.search), history);
      beaconNavigator
        .updateSearchParams({
          tab: targetTab,
          subtab: currentSubtab
        })
        .go();
    }
  }, [
    appName,
    urlTab,
    urlSubtab,
    currentRoute,
    currentSubtab,
    history,
    location.search,
    isRedirecting,
    isHiddenAdvertisingWorkflowSubtab
  ]);

  // Reset redirecting state when location changes (redirect completed)
  useEffect(() => {
    if (isRedirecting) {
      setIsRedirecting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return { isRedirecting };
};
